import { useState, useEffect, useCallback, useRef } from 'react';
import type { User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { authService } from '../services/authService';
import type { AuthUser, UserProfile } from '../services/authService';
import { db } from '../config/firebase';

export interface AuthState {
    user: AuthUser | null;
    currentUser: AuthUser | null; // Alias for user, for compatibility
    profile: UserProfile | null;
    loading: boolean;
    isAuthenticated: boolean;
}

export const useAuth = () => {
    const [authState, setAuthState] = useState<AuthState>({
        user: null,
        currentUser: null,
        profile: null,
        loading: true,
        isAuthenticated: false,
    });
    const fetchedProfilesRef = useRef<Set<string>>(new Set());
    const fetchingProfilesRef = useRef<Set<string>>(new Set());

    const fetchUserProfile = useCallback(async (force = false): Promise<UserProfile | null> => {
        if (!authState.user) {
            throw new Error('User not authenticated');
        }
        const userId = authState.user.uid;
        const alreadyFetched = fetchedProfilesRef.current.has(userId);

        if (!force && alreadyFetched && authState.profile) {
            return authState.profile;
        }

        if (fetchingProfilesRef.current.has(userId)) {
            return authState.profile ?? null;
        }

        try {
            fetchingProfilesRef.current.add(userId);
            const token = await authService.requireAuthToken({ allowUnverified: true });
            const userProfile = await authService.fetchUserProfile(token);
            fetchedProfilesRef.current.add(userId);
            setAuthState(prevState => ({ ...prevState, profile: userProfile }));
            return userProfile;
        } catch (error) {
            console.error("Failed to fetch user profile:", error);
            return null;
        } finally {
            fetchingProfilesRef.current.delete(userId);
        }
    }, [authState.user, authState.profile]);

    useEffect(() => {
        const unsubscribe = authService.onAuthStateChange(async (user: User | null) => {
            if (user) {
                // Se la verifica è appena stata completata, forziamo reload e refresh token
                const forceRefreshFlag = localStorage.getItem('forceTokenRefresh');
                if (forceRefreshFlag || user.emailVerified === false) {
                    try {
                        await user.reload();
                        await user.getIdToken(true);
                        if (localStorage.getItem('forceTokenRefresh')) {
                            localStorage.removeItem('forceTokenRefresh');
                        }
                    } catch (refreshErr) {
                        console.warn('Unable to refresh user/token on auth state change:', refreshErr);
                    }
                }

                const authUser: AuthUser = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    emailVerified: user.emailVerified,
                    getIdToken: () => user.getIdToken(),
                };
                setAuthState(prevState => ({
                    ...prevState,
                    user: authUser,
                    currentUser: authUser,
                    isAuthenticated: true,
                }));
                try {
                    const userId = user.uid;
                    if (fetchingProfilesRef.current.has(userId) || fetchedProfilesRef.current.has(userId)) {
                        setAuthState(prevState => ({ ...prevState, loading: false }));
                        return;
                    }

                    fetchingProfilesRef.current.add(userId);
                    const token = await authService.requireAuthToken({ allowUnverified: true });
                    const userProfile = await authService.fetchUserProfile(token);
                    fetchedProfilesRef.current.add(userId);
                    setAuthState(prevState => ({
                        ...prevState,
                        profile: userProfile,
                        loading: false,
                    }));
                } catch (error) {
                    console.error("Failed to fetch user profile on auth change:", error);
                    setAuthState(prevState => ({ ...prevState, loading: false }));
                } finally {
                    fetchingProfilesRef.current.delete(user.uid);
                }
            } else {
                fetchedProfilesRef.current.clear();
                fetchingProfilesRef.current.clear();
                setAuthState({
                    user: null,
                    currentUser: null,
                    profile: null,
                    loading: false,
                    isAuthenticated: false,
                });
            }
        });
        return () => unsubscribe();
    }, []);

    const sendSignInLink = async (email: string): Promise<boolean> => {
        try {
            return await authService.sendSignInLinkToEmail(email);
        } catch (error) {
            console.error('Error sending link:', error);
            throw error;
        }
    };

    const signOut = async (): Promise<void> => {
        try {
            await authService.signOut();
        } catch (error) {
            console.error('Logout error:', error);
            throw error;
        }
    };

    const signUpWithEmailAndPassword = async (email: string, password: string) => {
        try {
            return await authService.signUpWithEmailAndPassword(email, password);
        } catch (error) {
            console.error('Registration error:', error);
            throw error;
        }
    };

    const signInWithEmailAndPassword = async (email: string, password: string) => {
        try {
            return await authService.signInWithEmailAndPassword(email, password);
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const signInWithGoogle = async () => {
        try {
            return await authService.signInWithGoogle();
        } catch (error) {
            console.error('Google Sign-In error:', error);
            throw error;
        }
    };

    const sendPasswordResetEmail = async (email: string) => {
        try {
            return await authService.sendPasswordResetEmail(email);
        } catch (error) {
            console.error('Password reset error:', error);
            throw error;
        }
    };

    const checkUserExists = async (email: string): Promise<boolean> => {
        try {
            return await authService.checkUserExists(email);
        } catch (error) {
            // Se fallisce, logghiamo ma lasciamo gestire al chiamante
            console.error('Error in checkUserExists:', error);
            throw error;
        }
    };

    const checkUserProviders = async (email: string): Promise<{ exists: boolean; providers: string[] }> => {
        try {
            return await authService.checkUserProviders(email);
        } catch (error) {
            console.error('Error in checkUserProviders:', error);
            throw error;
        }
    };

    const getGoogleCredential = async () => {
        try {
            return await authService.getGoogleCredential();
        } catch (error) {
            console.error('Error getting Google credential:', error);
            throw error;
        }
    };

    const linkGoogleToAccount = async (email: string, password: string, googleCredential: any) => {
        try {
            return await authService.linkGoogleToAccount(email, password, googleCredential);
        } catch (error) {
            console.error('Error linking Google account:', error);
            throw error;
        }
    };

    const signInWithGoogleCredential = async (credential: any) => {
        try {
            return await authService.signInWithGoogleCredential(credential);
        } catch (error) {
            console.error('Error signing in with Google credential:', error);
            throw error;
        }
    };

    const fetchSignInMethods = async (email: string) => {
        try {
            return await authService.fetchSignInMethods(email);
        } catch (error) {
            console.error('Error fetching sign-in methods:', error);
            throw error;
        }
    };

    const setPasswordForCurrentUser = async (password: string) => {
        try {
            return await authService.setPasswordForCurrentUser(password);
        } catch (error) {
            console.error('Error setting password for current user:', error);
            throw error;
        }
    };

    const sendEmailVerification = async () => {
        try {
            return await authService.sendEmailVerification();
        } catch (error) {
            console.error('Error sending email verification:', error);
            throw error;
        }
    };

    const requestVerificationEmail = async () => {
        try {
            return await authService.requestVerificationEmail();
        } catch (error) {
            console.error('Error requesting verification email:', error);
            throw error;
        }
    };

    useEffect(() => {
        const currentUser = authState.user;
        if (!currentUser) {
            return;
        }

        const userDocRef = doc(db, 'users', currentUser.uid);
        const unsubscribeUser = onSnapshot(
            userDocRef,
            (docSnap) => {
                if (!docSnap.exists()) {
                    return;
                }

                const userData = docSnap.data();
                setAuthState(prevState => {
                    if (!prevState.user || prevState.user.uid !== currentUser.uid) {
                        return prevState;
                    }

                    const baseProfile: UserProfile = prevState.profile ?? {
                        profile: {
                            email: currentUser.email ?? '',
                            displayName: currentUser.displayName ?? undefined,
                        },
                        credits: {
                            balance: 0,
                            total_earned: 0,
                            total_spent: 0,
                        },
                    };

                    return {
                        ...prevState,
                        profile: {
                            ...baseProfile,
                            profile: {
                                ...baseProfile.profile,
                                ...userData,
                            },
                        },
                    };
                });
            },
            (error) => {
                console.error('Error updating profile in realtime:', error);
            }
        );

        const creditsDocRef = doc(db, 'user_credits', currentUser.uid);
        const unsubscribeCredits = onSnapshot(
            creditsDocRef,
            (docSnap) => {
                if (!docSnap.exists()) {
                    return;
                }

                const creditsData = docSnap.data();
                setAuthState(prevState => {
                    if (!prevState.user || prevState.user.uid !== currentUser.uid) {
                        return prevState;
                    }

                    const baseProfile: UserProfile = prevState.profile ?? {
                        profile: {
                            email: currentUser.email ?? '',
                            displayName: currentUser.displayName ?? undefined,
                        },
                        credits: {
                            balance: 0,
                            total_earned: 0,
                            total_spent: 0,
                        },
                    };

                    const nextCredits = {
                        balance: typeof creditsData.balance === 'number'
                            ? creditsData.balance
                            : baseProfile.credits.balance,
                        total_earned: typeof creditsData.total_earned === 'number'
                            ? creditsData.total_earned
                            : baseProfile.credits.total_earned,
                        total_spent: typeof creditsData.total_spent === 'number'
                            ? creditsData.total_spent
                            : baseProfile.credits.total_spent,
                    };

                    return {
                        ...prevState,
                        profile: {
                            ...baseProfile,
                            credits: nextCredits,
                        },
                    };
                });
            },
            (error) => {
                console.error('Error updating credits in realtime:', error);
            }
        );

        return () => {
            unsubscribeUser();
            unsubscribeCredits();
        };
    }, [authState.user]);

    return {
        ...authState,
        fetchUserProfile,
        sendSignInLink,
        signOut,
        signUpWithEmailAndPassword,
        signInWithEmailAndPassword,
        signInWithGoogle,
        sendPasswordResetEmail,
        checkUserExists,
        checkUserProviders,
        getGoogleCredential,
        linkGoogleToAccount,
        signInWithGoogleCredential,
        getCurrentUser: authService.getCurrentUser,
        fetchSignInMethods,
        setPasswordForCurrentUser,
        sendEmailVerification,
        requestVerificationEmail,
    };
};
